import type { UserProfile } from '../services/appsync-client'
import { canModerateForum as sharedCanModerateForum } from '@shared/rbac/forum-moderation'
import {
  ROLE_TYPES,
  displayRoleLabel,
  isIndependentUser,
  isMember,
  isMemberOwner,
  isMemberUserRole,
  isProvider,
  resolveProviderLevel,
  shouldUseSimplifiedNav,
} from '@shared/rbac/member-roles'

/**
 * The role model lives in the framework-agnostic `shared/rbac/member-roles.ts`
 * (the single source of truth shared with the lambdas). We re-export it here so
 * existing `../utils/rbac` imports keep working, and layer the web-only helpers
 * (nav, announcement audiences, admin gating) on top.
 */
export {
  ROLE_TYPES,
  displayRoleLabel,
  isIndependentUser,
  isMember,
  isMemberOwner,
  isMemberUserRole,
  isProvider,
  resolveProviderLevel,
}

/** Independent users and member users see simplified nav (sessions, direct, calls). */
export function shouldUseRoleSimplifiedNav(profile: Pick<UserProfile, 'roleType'>): boolean {
  return shouldUseSimplifiedNav(profile)
}

export function canCreateGroupChat(profile: Pick<UserProfile, 'roleType' | 'providerLevel'>): boolean {
  if (isIndependentUser(profile) || isMemberUserRole(profile)) return false
  return resolveProviderLevel(profile) >= 3
}

export function canAccessAdminPanel(profile: Pick<UserProfile, 'roleType' | 'providerLevel'>): boolean {
  return isProvider(profile) && resolveProviderLevel(profile) >= 3 && !isIndependentUser(profile)
}

/** Provider Owner (L4) only — gates the restructured Admin page and its entry points. */
export function isProviderOwner(profile: Pick<UserProfile, 'roleType' | 'providerLevel'>): boolean {
  return isProvider(profile) && resolveProviderLevel(profile) >= 4 && !isIndependentUser(profile)
}

export function canEditUserRoles(profile: Pick<UserProfile, 'roleType' | 'providerLevel'>): boolean {
  return resolveProviderLevel(profile) >= 4
}

/** Owner-level destructive user actions (remove from chat, ban/delete stubs). */
export function canManageUsers(profile: Pick<UserProfile, 'roleType' | 'providerLevel'>): boolean {
  return resolveProviderLevel(profile) >= 4
}

export function canManageGroupChat(profile: Pick<UserProfile, 'roleType' | 'providerLevel'>): boolean {
  return resolveProviderLevel(profile) >= 3
}

export type AnnouncementChannelSlug =
  | 'all'
  | 'staff-updates'
  | 'updates'
  | 'events'
  | 'jobs'
  | 'photo'
  | 'chitchat'
  | 'leave-updates'

export const ANNOUNCEMENT_POST_CHANNEL_SLUGS = [
  'staff-updates',
  'updates',
  'events',
  'jobs',
  'photo',
  'chitchat',
  'leave-updates',
] as const satisfies readonly Exclude<AnnouncementChannelSlug, 'all'>[]

/**
 * Per-channel READ audience — the authoritative source of truth (enforced in
 * both the UI and the AppSync resolvers). 'staff' = providers only; 'all' =
 * providers + members.
 *
 * Non-provider users (Members AND Independents) get ONLY the member Forum
 * (updates) — the Jobs board is staff-only, like every other wall channel.
 */
export const ANNOUNCEMENT_CHANNEL_READ_AUDIENCE: Record<
  Exclude<AnnouncementChannelSlug, 'all'>,
  'staff' | 'all'
> = {
  'staff-updates': 'staff',
  updates: 'all',
  jobs: 'staff',
  events: 'staff',
  chitchat: 'staff',
  photo: 'staff',
  'leave-updates': 'staff',
}

/** Whether the given user role may READ posts in a channel. */
export function canReadAnnouncementChannel(
  profile: Pick<UserProfile, 'roleType' | 'providerLevel'>,
  channelId: AnnouncementChannelSlug,
): boolean {
  if (channelId === 'all') return true
  const audience = ANNOUNCEMENT_CHANNEL_READ_AUDIENCE[channelId]
  if (audience === 'all') return true
  return isProvider(profile)
}

/** L3+ providers (admin) and L4 (owner) may create announcement posts. */
export function canManageAnnouncements(
  profile: Pick<UserProfile, 'roleType' | 'providerLevel'>,
): boolean {
  return isProvider(profile) && resolveProviderLevel(profile) >= 3
}

/** All signed-in roster users may comment on wall posts. */
export function canCommentOnAnnouncement(
  _profile: Pick<UserProfile, 'roleType' | 'providerLevel'>,
): boolean {
  return true
}

/** 1:1 voice calls — same roster access as starting a direct message. */
export function canInitiateVoiceCall(
  _profile: Pick<UserProfile, 'roleType' | 'providerLevel'>,
): boolean {
  return true
}

/** Per-channel posting rules — all channels require L3+ provider. */
export function canPostAnnouncementChannel(
  profile: Pick<UserProfile, 'roleType' | 'providerLevel'>,
  _channelId: AnnouncementChannelSlug,
): boolean {
  return canManageAnnouncements(profile)
}

/**
 * The single announcement channel that backs the member "Forum" — the
 * member-channel equivalent (label "Members", read audience "all"). Non-provider
 * users see ONLY this channel, presented as a single-channel Forum (no sidebar).
 */
export const MEMBER_FORUM_CHANNEL: AnnouncementChannelSlug = 'updates'

/**
 * Non-provider users (Members, Independents, legacy member owners/users, and
 * other association roles) get the moderated single-channel "Forum" in place
 * of announcements. Providers keep the multi-channel wall.
 */
export function isForumUser(profile: Pick<UserProfile, 'roleType'>): boolean {
  return !isProvider(profile)
}

/**
 * Who may moderate (approve / reject) member Forum submissions and receive the
 * awaiting-approval notification: active Provider Admins (L3) and Provider
 * Owners (L4). Same function the lambdas use (shared/rbac/forum-moderation).
 */
export function canModerateForum(
  profile: Pick<UserProfile, 'roleType' | 'providerLevel'> & { status?: string | null },
): boolean {
  return sharedCanModerateForum(profile)
}
